import { useCallback, useEffect, useRef, useState } from "react";

export type UseExpandableContentParams = {
  isExpanded: boolean;
};

export type UseExpandableContentResult = {
  innerRef: React.RefObject<HTMLDivElement | null>;
  containerHeight: number | string;
  onTransitionEnd: (event: React.TransitionEvent<HTMLDivElement>) => void;
  updateHeight: () => void;
};

export const useExpandableContent = ({ isExpanded }: UseExpandableContentParams): UseExpandableContentResult => {
  const innerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | string>(isExpanded ? "none" : 0);
  const wasExpanded = useRef(isExpanded);
  const cleanupRef = useRef<number | null>(null);

  const getInnerHeight = () => innerRef.current?.offsetHeight ?? 0;

  useEffect(() => {
    if (isExpanded === wasExpanded.current) return;

    if (!isExpanded) {
      setContainerHeight(getInnerHeight());
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      if (!isExpanded) {
        // Double rAF ensures the browser paints the explicit height
        // before transitioning to 0, which is needed when singleExpanded
        // implicitly collapses an item without a prior updateHeight() call.
        const innerFrameId = window.requestAnimationFrame(() => {
          setContainerHeight(0);
        });
        cleanupRef.current = innerFrameId;
      } else {
        setContainerHeight(getInnerHeight());
      }
    });

    wasExpanded.current = isExpanded;

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      if (cleanupRef.current) {
        window.cancelAnimationFrame(cleanupRef.current);
      }
    };
  }, [isExpanded]);

  const onTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;
      setContainerHeight(isExpanded ? "none" : 0);
    },
    [isExpanded]
  );

  const updateHeight = useCallback(() => {
    if (innerRef.current) {
      setContainerHeight(innerRef.current.offsetHeight);
    }
  }, []);

  return { innerRef, containerHeight, onTransitionEnd, updateHeight };
};
